import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, shouldRenderMobile, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsFormTechnicalVisitAPI } from '@philips-emr/hpms-provider-accreditation-components';
import { ActionAPI, DeckAPI, FetchAPI, FormAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnClick, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';

import { FragmentNames } from '../../codegen';
import { TechnicalVisitFormsAliases as Aliases } from '../../codegen/constants/aliases/technical-visit-forms-aliases';
import BaseController from '../BaseController';

import './technical-visit.css';

interface Form {
  nrSeqGroup: number,
  dsGroup: string,
  ieStatus: number,
  editable: string,
}

interface Response {
  response: string,
  status: string,
}

interface SubObject {
  code: number;
  description: string;
  ieRequired: string;
}

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_FORMS })
export default class TechnicalVisiFormsController extends BaseController {

  @Inject(Aliases.FETCH_QUESTIONS)
  private fetchQuestions: FetchAPI;

  @Inject(Aliases.TEXT_TITLE)
  private titleForm: TextAPI;

  @Inject(Aliases.PANEL_APPOINTMENT_DETAILS)
  private panelQuestions: PanelAPI;

  @Inject(Aliases.PANEL_ADITIONAL)
  private panelAditional: PanelAPI;

  @Inject(Aliases.FETCH_ATTACHMENTS)
  private fetchAttachments: FetchAPI;

  @Inject(Aliases.DECK_FORMS)
  private deck: DeckAPI;

  @Inject(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  private formQuestions: HpmsFormTechnicalVisitAPI;

  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;

  @Inject(Aliases.BUTTON_FINISH)
  private buttonFinish: ActionAPI;

  @Inject(Aliases.PANEL_UPLOAD)
  private panelUpload: PanelAPI;

  @OnMount([Aliases.PANEL_UPLOAD, Aliases.PANEL_TITLE_SEND_FILES])
  onMountPanelUpload(event: MountEvent): void {
    const api = event.target as PanelAPI;
    this.controlVisibilityPanels(api);
  }

  @OnMount(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  onMountQuestions(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
  }

  @OnSelectItem(Aliases.DECK_FORMS)
  onClickCard(event: SelectItemEvent<any>): void {
    const { nrSeqGroup, dsGroup } = event.item as Form;
    this.formSelected(dsGroup, nrSeqGroup);
  }

  @OnBeforeFetch(Aliases.FETCH_QUESTIONS)
  onBeforeFetchQuetions(event: FetchEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqGroup, nrSeqVisit, nrSeqForm } = this.context.getBulkData();
    api.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
  }

  @OnAfterFetch([Aliases.FETCH_FORMS, Aliases.FETCH_MENUS_MOBILE])
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as Form[];
    if (data) {
      const { nrSeqGroup, dsGroup } = data[0];
      const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
      this.fetchQuestions.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
      this.context.set({ nrSeqGroup, nrSeqVisit, dsGroup, nrSeqForm });
      this.fetchQuestions.forceFetch();
      this.titleForm.setValue(dsGroup);
      if (!shouldRenderMobile()) {
        this.deck.selectCard(String(nrSeqGroup));
      } else {
        this.formMenuMobile.setData({ menuMobile: { dsGroup, nrSeqGroup } });
      }
    }
  }

  @OnAfterFetch(Aliases.FETCH_QUESTIONS)
  onAfterFetchQuestionary(): void {
    const { dsGroup } = this.context.getBulkData() as unknown as Form;
    this.titleForm.setValue(dsGroup);
  }

  @OnMount(Aliases.PANEL_MOBILE)
  onMountPanelMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  @OnMount([Aliases.FETCH_FORMS, Aliases.FETCH_MENUS_MOBILE])
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqForm } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqForm });
    api.forceFetch();
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onchange(event: ChangeEvent<{ dsGroup: string, nrSeqGroup: number }>): void {
    this.formSelected(event.value.dsGroup, event.value.nrSeqGroup);
  }

  @OnMount(Aliases.BUTTON_FINISH)
  onMountButtomFinish(event: MountEvent): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    const api = event.target as ActionAPI;
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { editable } = response.body;
        api.setEnabled(editable === 'S');
      }
    });
  }

  @OnSubmit(Aliases.FORM_FILES)
  onSubmitAttachments(event: SubmitEvent): void {
    const record = event.record;
    const api = event.target as FormAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const body = { ...record, nrSeqVisit };
    void HttpService.post(Endpoint.POST.TECHNICAL_VISIT_ATTACHMENTS, {}, { body }).then(() => {
      this.fetchAttachments.setParameters({ nrSeqVisit });
      this.fetchAttachments.forceFetch();
      api.setRecord({});
    });
  }

  @OnMount(Aliases.FETCH_ATTACHMENTS)
  onMountFetchAttachements(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnClick(Aliases.BUTTON_FINISH)
  onClickFinish(): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.TECHNICAL_VISIT_FINISH, { nrSeqVisit, nrSeqForm }).then(() => {
      this.buttonFinish.setEnabled(false);
      this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
      catchRenderException(() => this.controlVisibilityPanels(this.panelUpload));
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-sucessfull'));
    }).catch((resp) => {
      if (resp.body) {
        const { response } = resp.body as unknown as Response;
        ToastServiceProviderAccreditation.error(response);
      }
    });
  }

  @OnSelectItem(Aliases.DECK_ATTACHMENTS)
  onClickDownloadAttachment(event: SelectItemEvent<any>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSequence, dsAttachment } = event.item;
    const api = event.target as DeckAPI;
    api.unselectAll();
    ToastServiceProviderAccreditation.informationBubble(MessageService.translate('pa.download-initiated'));
    FileService.download(Endpoint.GET.TECHNICAL_VISIT_ATTACHMENT_DOWNLOAD, dsAttachment,
      { nrSeqAttachment: nrSequence, nrSeqVisit }, HttpMethod.GET)
      .catch(() => {
        ToastServiceProviderAccreditation.close();
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-finding-file'));
      });
  }

  @OnSubmit(Aliases.HPMS_FORM_TECHNICAL_VISIT)
  public onSubmitForm(event: FormAPI): void {
    this.handlerSubmit(event);
  }

  handlerSubmit(event: FormAPI): void {
    const recordsDsjustification = [];
    const recordsAutoComplete: { nrSeqFormPr: number; nrSeqAnswer: number | null; }[] = [];
    let nrSeqFormPr, nrSeqAnswer;

    for (const [key, value] of Object.entries(event.record)) {

      if (!key.includes('label__')) {

        if (key.includes('answers__')) {
          nrSeqFormPr = Number(key.split('answers__')[1]);
          nrSeqAnswer = (value as SubObject).code || null;
          recordsAutoComplete.push({ 'nrSeqFormPr': nrSeqFormPr, 'nrSeqAnswer': nrSeqAnswer });
        }

        if (key.includes('justification__')) {
          nrSeqFormPr = Number(key.split('justification__')[1]);
          recordsDsjustification.push({ 'nrSeqFormPr': nrSeqFormPr, 'dsJustification': value });
        }
      }
    }

    const answersForm = recordsDsjustification.map((justification) => ({
      ...recordsAutoComplete.find((o) => o.nrSeqFormPr === justification.nrSeqFormPr),
      ...justification,
    })) as unknown as any;

    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.FORM_TECHNICAL_VISIT, { nrSeqVisit }, { body: answersForm }).then(() => {
      this.fetchQuestions.forceFetch();
      this.formQuestions.setEditing(false);
      this.formQuestions.setLocked(false);
    });
  }

  private controlVisibilityPanels(api: PanelAPI): void {
    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { editable } = response.body;
        api.setVisible(editable == 'S');
      }
    });
  }

  private formSelected(dsGroup: string, nrSeqGroup: number): void {

    const { nrSeqVisit, nrSeqForm } = this.historyService.getCurrentRouteParams();

    catchRenderException(() => {
      if (nrSeqGroup) {
        this.panelQuestions.setVisible(true);
        this.panelAditional.setVisible(false);
        this.context.set({ nrSeqGroup, nrSeqVisit, dsGroup });
        this.titleForm.setValue(dsGroup);
        this.fetchQuestions.setParameters({ nrSeqGroup, nrSeqVisit, nrSeqForm });
        this.fetchQuestions.forceFetch();
        this.controlEditing(Number(nrSeqVisit), Number(nrSeqForm));
      } else {
        this.panelQuestions.setVisible(false);
        this.panelAditional.setVisible(true);
      }
    });
  }

  private controlEditing(nrSeqVisit: number, nrSeqForm: number): void {
    void HttpService.get<Form>(Endpoint.GET.FORM_TECHNICAL_VISIT_DATA, { nrSeqVisit, nrSeqForm }).then((response) => {
      if (response.body) {
        const { editable } = response.body;
        catchRenderException(() => {
          this.formQuestions.setEditing(editable == 'S');
        });
      }
    });
  }
}
